import { useEffect, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { DraggableData } from 'react-draggable';
import {
  angleBetweenPoints,
  distanceBetweenPoints,
} from 'features/map/mapUtils';
import PanelLineDraggablePoint from 'features/map/PanelLineDraggablePoint';
import { RoofIcon } from 'ui/icons/Icons';
import { PanelLineState } from 'schemas/panelLineStateSchema';

type Props = {
  metersPerPixel: number;
  panelSizeInMeter: { w: number; h: number };
  scaleRatio: number;
  initialState: PanelLineState;
  onChangeEnd?: (data: PanelLineState) => void;
  onSelect?: (panelLineId: string) => void;
  previewOnly?: boolean;
  selected?: boolean;
};

const PanelLine = ({
  metersPerPixel,
  panelSizeInMeter,
  scaleRatio,
  initialState,
  onChangeEnd,
  onSelect,
  selected = false,
  previewOnly = false,
}: Props) => {
  const point1ItemRef = useRef(null);
  const point2ItemRef = useRef(null);
  const [state, setState] = useState<PanelLineState>(initialState);
  const { point1, point2 } = state;

  const currentColor = selected ? 'primaryColor' : 'grey';

  const lineWidth = distanceBetweenPoints(point1, point2);
  const angle = angleBetweenPoints(point1, point2);

  const margin = 50;
  const panelSpace = 1;

  const panelWidth =
    (state.orientation === 'horizontal'
      ? panelSizeInMeter.h
      : panelSizeInMeter.w) / metersPerPixel;
  const panelHeight =
    (state.orientation === 'horizontal'
      ? panelSizeInMeter.w
      : panelSizeInMeter.h) / metersPerPixel;

  const numPanel = Math.floor((lineWidth - margin * 1.8) / (panelWidth + 1)); // * 1.8 instead of * 2 to have a better UX when dragging panels

  // --------------------- Methods ------------------------

  const togglePanel = (index: number) => {
    const newHiddenPanels = [...state.hiddenPanels];
    if (!newHiddenPanels.includes(index)) {
      newHiddenPanels.push(index);
    } else {
      newHiddenPanels.splice(newHiddenPanels.indexOf(index), 1);
    }

    const newState = { ...state, hiddenPanels: newHiddenPanels };
    setState(newState);
    onChangeEnd?.(newState);
  };

  const handleItemDragMove = (
    index: number,
    dragData: DraggableData,
    changeCallback?: (state: PanelLineState) => void
  ) => {
    const newPoint = { x: dragData.x, y: dragData.y };
    const newPoint1 = index === 0 ? newPoint : point1;
    const newPoint2 = index === 1 ? newPoint : point2;

    const newState: PanelLineState = {
      ...state,
      point1: newPoint1,
      point2: newPoint2,
      hiddenPanels: state.hiddenPanels,
      angle,
      panelAmount: numPanel,
    };
    setState(newState);
    changeCallback?.(newState);
  };

  // --------------------- effect ------------------------

  // initial check/security for the amount of panels and hidden panels
  useEffect(() => {
    if (
      !previewOnly &&
      state &&
      angle !== undefined &&
      numPanel !== undefined &&
      (state.angle === undefined || state.panelAmount === undefined)
    ) {
      const newState: PanelLineState = {
        ...state,
        hiddenPanels: state.hiddenPanels,
        angle,
        panelAmount: numPanel,
      };
      setState(newState);
      onChangeEnd?.(newState);
    }
  }, [previewOnly, state, numPanel, angle, onChangeEnd]);

  // notify when panel amount is changed
  useEffect(() => {
    // TODO: check here for performance on panel line drag...
    if (state && state.angle && state.panelAmount !== numPanel) {
      const newState = { ...state, panelAmount: numPanel };
      setState(newState);
      onChangeEnd?.(newState);
    }
  }, [state, numPanel, onChangeEnd]);

  // --------------------- render ------------------------

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      style={{
        position: 'absolute',
        pointerEvents: previewOnly ? 'none' : 'auto',
        // background: 'rgb(0,0,0,0.2)',
        // width: '100px',
        // height: '100px',
        fontSize: '50px',
        // pointerEvents: 'auto',
      }}
      onMouseDown={() => {
        if (!previewOnly) {
          onSelect?.(state.id);
        }
      }}
    >
      {!previewOnly && (
        <>
          <PanelLineDraggablePoint
            initPos={initialState.point1}
            nodeRef={point1ItemRef}
            scaleRatio={scaleRatio}
            color={currentColor}
            opacity={selected ? 1 : 0.7}
            onChange={(data) => handleItemDragMove(0, data)}
            onChangeStart={(data) => handleItemDragMove(0, data)}
            onChangeEnd={(data) => handleItemDragMove(0, data, onChangeEnd)}
          />
          <PanelLineDraggablePoint
            initPos={initialState.point2}
            nodeRef={point2ItemRef}
            scaleRatio={scaleRatio}
            color={currentColor}
            opacity={selected ? 1 : 0.7}
            onChange={(data) => handleItemDragMove(1, data)}
            onChangeStart={(data) => handleItemDragMove(1, data)}
            onChangeEnd={(data) => handleItemDragMove(1, data, onChangeEnd)}
          />
        </>
      )}
      {numPanel > 0 && (
        <Box
          style={{
            position: 'absolute',
            transformOrigin: 'top left',
            transform: `rotate(${angle.toFixed(1)}deg)`,
            // height: 5,
            // backgroundColor: 'green',
            zIndex: -2,
            left: point1.x,
            top: point1.y - 2,
            width: lineWidth,
            // pointerEvents: 'none',
          }}
          className="lineOfPanels"
        >
          {!previewOnly && (
            <>
              <RoofIcon
                position="absolute"
                left="calc(50% - 10px)"
                top={panelHeight / 2}
                boxSize="20px"
                pointerEvents="none"
              />

              <Box
                width={margin}
                position="absolute"
                height={1}
                top={0}
                bgColor={currentColor}
                pointerEvents="none"
              />
              <Box
                width={
                  lineWidth - margin - (panelWidth + panelSpace) * numPanel
                }
                position="absolute"
                right={0}
                top={0}
                height={1}
                bgColor={currentColor}
                opacity={selected ? 1 : 0.2}
                pointerEvents="none"
              />
            </>
          )}

          {Array(numPanel)
            .fill('')
            .map((_item, i) => (
              <Box
                // eslint-disable-next-line
                key={`panel_${i}`}
                sx={{
                  position: 'absolute',
                  width: panelWidth,
                  height: panelHeight,
                  backgroundColor: 'black',
                  opacity: state.hiddenPanels.includes(i) ? '0.2' : '0.65',
                  top: `${-panelHeight / 2}px`,
                  left: margin + i * (panelWidth + panelSpace),
                }}
                border="1px"
                borderColor="selectionColor"
                _hover={{
                  base: {},
                  md: {
                    opacity: '0.85',
                    border: '1px',
                    borderColor: 'white',
                    backgroundColor: state.hiddenPanels.includes(i)
                      ? 'white'
                      : 'selectionColor',
                  },
                }}
                onClick={() => {
                  togglePanel(i);
                }}
                onKeyDown={() => {
                  togglePanel(i);
                }}
                role="button"
                tabIndex={i}
                aria-label={`panel_${i}`}
              />
            ))}

          {/* 
          DETAILS: not used anymore
          <div
            style={{
              position: 'absolute',
              width: lineWidth,
              // backgroundColor: 'yellow',
              color: 'white',
              opacity: 1,
              textAlign: 'center',
              fontSize: '20px',
            }}
          >
            {(lineWidth * metersPerPixel).toFixed(1)}m = {numPanel}
          </div> */}
        </Box>
      )}
    </div>
  );
};

export default PanelLine;
